import React, { useEffect, useState } from "react";
import {
  Grid, Card, CardContent, Typography, Box,
  CircularProgress, Backdrop, IconButton, Tooltip, Divider,
} from "@mui/material";
import {
  AccountBalanceWallet, Api, AttachMoney, SupportAgent, Refresh,
  TrendingUp, CheckCircle
} from "@mui/icons-material";
import { getMerchantDashboard } from "../../services/api";

const EnhancedDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => { fetchDashboard(); }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const res = await getMerchantDashboard();
      setDashboardData(res.data);
    } catch (err) {
      console.error("Dashboard error:", err);
    } finally {
      setLoading(false);
    }
  };

  const MetricCard = ({ title, value, subtitle, icon, accentColor, gradient }) => (
    <Card sx={{
      borderRadius: 3,
      backgroundColor: "#ffffff",
      border: "1px solid #f1f5f9",
      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      position: "relative",
      overflow: "hidden",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: "0 12px 28px rgba(0,0,0,0.10)",
        "& .metric-icon": { transform: "scale(1.1)" },
      },
    }}>
      {/* Gradient top bar */}
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: gradient }} />
      <CardContent sx={{ p: { xs: 2, sm: 2.5 }, flexGrow: 1, display: "flex", flexDirection: "column", pt: { xs: 2.5, sm: 3 } }}>
        {/* Icon */}
        <Box className="metric-icon" sx={{
          width: { xs: 36, sm: 42 }, height: { xs: 36, sm: 42 }, borderRadius: "10px",
          background: `${accentColor}12`,
          display: "flex", alignItems: "center", justifyContent: "center",
          mb: 2, transition: "all 0.3s ease", flexShrink: 0,
        }}>
          {React.cloneElement(icon, { sx: { fontSize: { xs: 18, sm: 22 }, color: accentColor } })}
        </Box>

        <Typography variant="h4" sx={{
          fontWeight: 800, color: "#0f172a", letterSpacing: "-0.02em",
          mb: 0.5, fontSize: { xs: "1.15rem", sm: "1.6rem" },
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }} title={value}>
          {value}
        </Typography>

        <Typography variant="body2" sx={{
          color: "#475569", fontWeight: 600, fontSize: { xs: "0.72rem", sm: "0.82rem" }, lineHeight: 1.3, mb: 1,
        }}>
          {title}
        </Typography>

        {subtitle && (
          <Typography variant="caption" sx={{
            color: "#94a3b8", mt: "auto", display: "block",
            fontSize: { xs: "0.62rem", sm: "0.7rem" }, fontWeight: 500, lineHeight: 1.2,
          }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const cards = [
    {
      title: "Wallet Balance", subtitle: `Lien: ₹${dashboardData?.lienAmount?.toLocaleString("en-IN") ?? 0}`,
      value: `₹${dashboardData?.walletBalance?.toLocaleString("en-IN") ?? 0}`,
      icon: <AccountBalanceWallet />, accentColor: "#6366f1",
      gradient: "linear-gradient(90deg, #6366f1, #8b5cf6)"
    },
    {
      title: "Usable Balance", subtitle: "Available for transactions",
      value: `₹${dashboardData?.usableBalance?.toLocaleString("en-IN") ?? 0}`,
      icon: <AttachMoney />, accentColor: "#10b981",
      gradient: "linear-gradient(90deg, #10b981, #34d399)"
    },
    {
      title: "Total API Calls", subtitle: "All-time usage",
      value: dashboardData?.totalApiCalls ?? 0,
      icon: <Api />, accentColor: "#f59e0b",
      gradient: "linear-gradient(90deg, #f59e0b, #fbbf24)"
    },
    {
      title: "Enabled APIs", subtitle: "Active integrations",
      value: dashboardData?.enabledApisCount ?? 0,
      icon: <Api />, accentColor: "#06b6d4",
      gradient: "linear-gradient(90deg, #06b6d4, #38bdf8)"
    },
    {
      title: "Recharge Requests", subtitle: "Pending approvals",
      value: dashboardData?.pendingRechargeRequests ?? 0,
      icon: <AttachMoney />, accentColor: "#ec4899",
      gradient: "linear-gradient(90deg, #ec4899, #f472b6)"
    },
    {
      title: "Open Support Tickets", subtitle: "Active tickets",
      value: dashboardData?.openTickets ?? 0,
      icon: <SupportAgent />, accentColor: "#8b5cf6",
      gradient: "linear-gradient(90deg, #8b5cf6, #a78bfa)"
    },
  ];

  return (
    <>
      <Backdrop open={loading} sx={{ color: "#fff", zIndex: 1300 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box sx={{ p: { xs: 1, sm: 0 } }}>
        {/* Header */}
        <Box sx={{
          mb: 3.5, display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" }, gap: 2
        }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a", fontSize: { xs: "1.1rem", sm: "1.2rem" }, letterSpacing: '-0.01em' }}>
              Overview
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b", fontSize: { xs: "0.78rem", sm: "0.85rem" }, mt: 0.3 }}>
              Wallet, API and support summary
            </Typography>
          </Box>
          <Tooltip title="Refresh data">
            <IconButton onClick={fetchDashboard} sx={{
              backgroundColor: "#ffffff", border: "1px solid #e5e7eb",
              width: 36, height: 36,
              "&:hover": { backgroundColor: "rgba(6,182,212,0.06)", borderColor: "#06b6d4", color: "#06b6d4" },
              ml: { xs: "auto", sm: 0 }, transition: 'all 0.2s ease'
            }}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Cards */}
        <Grid container spacing={{ xs: 1.5, sm: 2.5 }}>
          {cards.map((card, i) => (
            <Grid item xs={6} sm={6} md={4} lg={2} key={i} sx={{ display: 'flex' }}>
              <MetricCard {...card} />
            </Grid>
          ))}
        </Grid>

        {/* Quick tip banner */}
        {dashboardData && (
          <Box sx={{
            mt: 3.5, p: 2.5, borderRadius: 3,
            background: 'linear-gradient(135deg, rgba(6,182,212,0.07) 0%, rgba(59,130,246,0.05) 100%)',
            border: '1px solid rgba(6,182,212,0.15)',
            display: 'flex', alignItems: 'center', gap: 2
          }}>
            <Box sx={{ width: 34, height: 34, borderRadius: '50%', background: 'rgba(6,182,212,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <TrendingUp sx={{ fontSize: 18, color: '#06b6d4' }} />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a', fontSize: '0.82rem' }}>
                Platform is running smoothly
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b' }}>
                All your API integrations are active. Visit Services Report for detailed transaction history.
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};

export default EnhancedDashboard;
